import { useState, useRef, useEffect } from "react";
import { Globe } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Language selector that enables Google Translate for the page.
 * This approach keeps the site in English as the source language
 * and lets users translate it client-side into 45+ languages.
 * 
 * How it works:
 * 1. On first click, loads the Google Translate script
 * 2. Google Translate adds a hidden element that manages translation
 * 3. User selects a language from the dropdown
 * 4. Google Translate translates all visible text on the page
 * 
 * This is the most cost-effective approach for global i18n:
 * - No server-side translation needed
 * - No translation files to maintain
 * - Covers 100+ languages automatically
 * - Works with dynamically loaded content
 */

const POPULAR_LANGUAGES = [
  { code: "en", name: "English", native: "English", flag: "🇺🇸" },
  { code: "es", name: "Spanish", native: "Español", flag: "🇪🇸" },
  { code: "pt", name: "Portuguese", native: "Português", flag: "🇧🇷" },
  { code: "fr", name: "French", native: "Français", flag: "🇫🇷" },
  { code: "de", name: "German", native: "Deutsch", flag: "🇩🇪" },
  { code: "it", name: "Italian", native: "Italiano", flag: "🇮🇹" },
  { code: "nl", name: "Dutch", native: "Nederlands", flag: "🇳🇱" },
  { code: "pl", name: "Polish", native: "Polski", flag: "🇵🇱" },
  { code: "ja", name: "Japanese", native: "日本語", flag: "🇯🇵" },
  { code: "ko", name: "Korean", native: "한국어", flag: "🇰🇷" },
  { code: "zh-CN", name: "Chinese (Simplified)", native: "简体中文", flag: "🇨🇳" },
  { code: "zh-TW", name: "Chinese (Traditional)", native: "繁體中文", flag: "🇹🇼" },
  // RTL languages (ar, he, fa, ur) are hidden for now — the layout uses
  // physical CSS utilities that don't mirror under dir="rtl".
  { code: "hi", name: "Hindi", native: "हिन्दी", flag: "🇮🇳" },
  { code: "id", name: "Indonesian", native: "Bahasa Indonesia", flag: "🇮🇩" },
  { code: "th", name: "Thai", native: "ไทย", flag: "🇹🇭" },
  { code: "vi", name: "Vietnamese", native: "Tiếng Việt", flag: "🇻🇳" },
  { code: "tr", name: "Turkish", native: "Türkçe", flag: "🇹🇷" },
  { code: "ms", name: "Malay", native: "Bahasa Melayu", flag: "🇲🇾" },
  { code: "tl", name: "Filipino", native: "Filipino", flag: "🇵🇭" },
  { code: "sw", name: "Swahili", native: "Kiswahili", flag: "🇰🇪" },
  { code: "ru", name: "Russian", native: "Русский", flag: "🇷🇺" },
  { code: "uk", name: "Ukrainian", native: "Українська", flag: "🇺🇦" },
  { code: "ro", name: "Romanian", native: "Română", flag: "🇷🇴" },
  { code: "hu", name: "Hungarian", native: "Magyar", flag: "🇭🇺" },
  { code: "cs", name: "Czech", native: "Čeština", flag: "🇨🇿" },
  { code: "sv", name: "Swedish", native: "Svenska", flag: "🇸🇪" },
  { code: "da", name: "Danish", native: "Dansk", flag: "🇩🇰" },
  { code: "no", name: "Norwegian", native: "Norsk", flag: "🇳🇴" },
  { code: "fi", name: "Finnish", native: "Suomi", flag: "🇫🇮" },
  { code: "el", name: "Greek", native: "Ελληνικά", flag: "🇬🇷" },
  { code: "bn", name: "Bengali", native: "বাংলা", flag: "🇧🇩" },
  { code: "ta", name: "Tamil", native: "தமிழ்", flag: "🇮🇳" },
];

// RTL languages that need dir="rtl" on the document
const RTL_LANGUAGES = new Set(["ar", "he", "fa", "ur"]);

// Persisted selection so the picker reflects the active language after reloads
const LANG_STORAGE_KEY = "gp:language";

function readStoredLanguage(): string {
  try {
    return window.localStorage.getItem(LANG_STORAGE_KEY) || "en";
  } catch {
    return "en";
  }
}

export default function LanguageSelector({ variant = "default" }: { variant?: "default" | "compact" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState(readStoredLanguage);
  const [translateLoaded, setTranslateLoaded] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Restore RTL direction when a persisted language needs it
  useEffect(() => {
    if (RTL_LANGUAGES.has(selectedLang)) {
      // Only apply if the googtrans cookie confirms translation is active
      const hasGoogtrans = document.cookie.includes("googtrans=");
      if (hasGoogtrans) {
        document.documentElement.dir = "rtl";
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Load Google Translate script on demand
  const loadGoogleTranslate = () => {
    if (translateLoaded) return;
    
    // Create the hidden container for Google Translate
    const container = document.createElement("div");
    container.id = "google_translate_element";
    container.style.display = "none";
    document.body.appendChild(container);

    // Load the script
    const script = document.createElement("script");
    script.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    script.async = true;
    script.onload = () => setTranslateLoaded(true);
    document.body.appendChild(script);
  };

  const selectLanguage = (langCode: string) => {
    setSelectedLang(langCode);
    setIsOpen(false);
    try {
      window.localStorage.setItem(LANG_STORAGE_KEY, langCode);
    } catch {
      // Storage unavailable (private mode) — selection is session-only
    }

    // Handle RTL/LTR direction
    const isRtl = RTL_LANGUAGES.has(langCode);
    document.documentElement.dir = isRtl ? "rtl" : "ltr";

    if (langCode === "en") {
      // Reset to English — remove Google Translate
      const frame = document.querySelector(".goog-te-banner-frame") as HTMLIFrameElement;
      if (frame) {
        const closeBtn = frame.contentDocument?.querySelector(".goog-close-link") as HTMLElement;
        closeBtn?.click();
      }
      // Also try cookie-based reset
      document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=." + window.location.hostname;
      window.location.reload();
      return;
    }

    // Load Google Translate if not loaded
    if (!translateLoaded) {
      loadGoogleTranslate();
    }

    // Set the translation cookie and trigger translation
    document.cookie = `googtrans=/en/${langCode}; path=/;`;
    document.cookie = `googtrans=/en/${langCode}; path=/; domain=.${window.location.hostname}`;

    // Trigger Google Translate via the hidden select element
    setTimeout(() => {
      const select = document.querySelector(".goog-te-combo") as HTMLSelectElement;
      if (select) {
        select.value = langCode;
        select.dispatchEvent(new Event("change"));
      } else {
        // If Google Translate hasn't initialized yet, reload
        window.location.reload();
      }
    }, 500);
  };

  const currentLang = POPULAR_LANGUAGES.find(l => l.code === selectedLang);

  if (variant === "compact") {
    return (
      <div className="relative notranslate" ref={dropdownRef}>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            if (!translateLoaded) loadGoogleTranslate();
            setIsOpen(!isOpen);
          }}
          className="gap-1.5 text-slate-400 hover:text-white px-2"
          aria-label="Select language"
        >
          <Globe className="w-4 h-4" />
          <span className="text-xs">{currentLang?.flag ?? "🌐"}</span>
        </Button>

        {isOpen && (
          <div className="absolute bottom-full mb-2 right-0 w-64 max-h-80 overflow-y-auto bg-[#141828] border border-white/10 rounded-lg shadow-xl z-50">
            <div className="p-2 border-b border-white/5">
              <p className="text-xs text-slate-500 px-2 py-1">Select Language</p>
            </div>
            <div className="p-1">
              {POPULAR_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => selectLanguage(lang.code)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
                    selectedLang === lang.code
                      ? "bg-neon/10 text-neon"
                      : "text-slate-300 hover:bg-white/5 hover:text-white"
                  }`}
                >
                  <span className="text-base">{lang.flag}</span>
                  <span className="flex-1 text-left">{lang.native}</span>
                  <span className="text-xs text-slate-500">{lang.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative notranslate" ref={dropdownRef}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          if (!translateLoaded) loadGoogleTranslate();
          setIsOpen(!isOpen);
        }}
        className="gap-2 border-white/10 text-slate-300 hover:text-white hover:bg-white/5"
        aria-label="Select language"
      >
        <Globe className="w-4 h-4" />
        <span>{currentLang?.native ?? "English"}</span>
      </Button>

      {isOpen && (
        <div className="absolute top-full mt-2 right-0 w-72 max-h-96 overflow-y-auto bg-[#141828] border border-white/10 rounded-lg shadow-xl z-50">
          <div className="p-3 border-b border-white/5">
            <p className="text-sm font-medium text-white">Select Language</p>
            <p className="text-xs text-slate-500 mt-0.5">Page will be translated automatically</p>
          </div>
          <div className="p-1 grid grid-cols-1">
            {POPULAR_LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => selectLanguage(lang.code)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
                  selectedLang === lang.code
                    ? "bg-neon/10 text-neon"
                    : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`}
              >
                <span className="text-base">{lang.flag}</span>
                <span className="flex-1 text-left">{lang.native}</span>
                <span className="text-xs text-slate-500">{lang.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
